import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';

// ✅ Import all your components
import Login from './components/Login';
import StudentDashboard from './components/StudentDashboard';
import Exam from './components/Exam';
import AdminDashboard from './components/AdminDashboard';
import AdminEnglishView from './components/AdminEnglishView';
import AdminMathView from './components/AdminMathView';
import AdminScienceView from './components/AdminScienceView';
import AddStudent from './components/AddStudent';
import ManageStudent from './components/ManageStudent';
import ExamBuilder from './components/ExamBuilder';
import ViewExams from './components/ViewExams';
import ExamPreview from './components/ExamPreview';
import AssignExam from './components/AssignExam';
import StudentExamPlayer from './components/StudentExamPlayer';
import ReviewExams from './components/ReviewExams';
import ExamResultsDetails from './components/ExamResultsDetails';
import ExamAttemptDetails from './components/ExamAttemptDetails'; // Keep this for now
import ReviewAttemptDetails from './components/ReviewAttemptDetails'; // ✅ Handles Math & English
import EnglishQuestionUpload from './components/EnglishQuestionUpload';
import MathQuestionUpload from './components/MathQuestionUpload';

import AdaptiveExamBuilder from './components/AdaptiveExamBuilder';
import ExamFlowBuilder from './components/ExamFlowBuilder';
import FlowBuilderExams from './components/FlowBuilderExams';
import FlowExamPreview from './components/FlowExamPreview';
import ExamLauncher from './components/ExamLauncher';
import BandProgressionExam from './components/BandProgressionExam';
import MathDiagnosticPlayer from './components/MathDiagnosticPlayer';
import MathExamFlowBuilder from './components/MathExamFlowBuilder';

import ScienceExamFlowBuilder from './components/ScienceExamFlowBuilder';
import ScienceFlowBuilderExams from './components/ScienceExamFlowBuilder';
import ScienceQuestionUpload from './components/ScienceQuestionUpload';

import AdminAssignStudentToParent from './components/AdminAssignStudentToParent';
import AdminCreateParent from './components/AdminCreateParent';
import AdminManageParent from './components/AdminManageParent';

import ParentDashboard from './components/ParentDashboard';
import ParentResults from './components/ParentResults';

import ManageTags from './components/ManageTags';

import AdminTutorManagement from './components/AdminTutorManagement';

// ✅ Tutor frontend
import TutorDashboard from './components/TutorDashboard';
import TutorStudentDetail from './components/TutorStudentDetail';

// ====================
// ✅ AuthGuard Component (role-aware redirect)
// ====================
function AuthGuard({ children, requiredRole }) {
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userRaw = localStorage.getItem('user');

    if (!token) {
      localStorage.setItem('redirectAfterLogin', location.pathname);
      window.location.href = '/login';
      return;
    }

    if (requiredRole && userRaw) {
      try {
        const user = JSON.parse(userRaw);
        if (user.role !== requiredRole) {
          const homeByRole = {
            admin: '/admin/dashboard',
            parent: '/parent/dashboard',
            tutor: '/tutor/dashboard',
            student: '/student/dashboard'
          };
          window.location.href = homeByRole[user.role] || '/login';
          return;
        }
      } catch (e) {
        console.error('Failed to parse user:', e);
      }
    }
  }, [location.pathname, requiredRole]);

  return children;
}

// ====================
// ✅ PublicRoute Component
// ====================
function PublicRoute({ children }) {
  useEffect(() => {
    localStorage.removeItem('redirectAfterLogin');
  }, []);
  return children;
}

// ====================
// ✅ Main App Component
// ====================
function App() {
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token' && !e.newValue && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />

        <Route path="/student/dashboard" element={<AuthGuard requiredRole="student"><StudentDashboard /></AuthGuard>} />
        <Route path="/student/exam" element={<AuthGuard requiredRole="student"><Exam /></AuthGuard>} />
        <Route path="/student/start-exam/:examId" element={<AuthGuard requiredRole="student"><ExamLauncher /></AuthGuard>} />
        <Route path="/student/band-exam/:examId" element={<AuthGuard requiredRole="student"><BandProgressionExam /></AuthGuard>} />
        <Route path="/student/exam-player/:examId" element={<AuthGuard requiredRole="student"><Exam /></AuthGuard>} />
        <Route path="/student/math-exam/:examId" element={<AuthGuard requiredRole="student"><MathDiagnosticPlayer /></AuthGuard>} />

        <Route path="/admin/dashboard" element={<AuthGuard requiredRole="admin"><AdminDashboard /></AuthGuard>} />
        <Route path="/admin/questions/english" element={<AuthGuard requiredRole="admin"><AdminEnglishView /></AuthGuard>} />
        <Route path="/admin/questions/math" element={<AuthGuard requiredRole="admin"><AdminMathView /></AuthGuard>} />
        <Route path="/admin/questions/science" element={<AuthGuard requiredRole="admin"><AdminScienceView /></AuthGuard>} />
        <Route path="/admin/add-student" element={<AuthGuard requiredRole="admin"><AddStudent /></AuthGuard>} />
        <Route path="/admin/manage-student" element={<AuthGuard requiredRole="admin"><ManageStudent /></AuthGuard>} />
        <Route path="/admin/exam-builder" element={<AuthGuard requiredRole="admin"><ExamBuilder /></AuthGuard>} />
        <Route path="/admin/view-exams" element={<AuthGuard requiredRole="admin"><ViewExams /></AuthGuard>} />
        <Route path="/admin/exam-preview/:id" element={<AuthGuard requiredRole="admin"><ExamPreview /></AuthGuard>} />
        <Route path="/admin/assign-exam" element={<AuthGuard requiredRole="admin"><AssignExam /></AuthGuard>} />
        <Route path="/admin/review-exams" element={<AuthGuard requiredRole="admin"><ReviewExams /></AuthGuard>} />
        <Route path="/admin/review-exams/:examId" element={<AuthGuard requiredRole="admin"><ExamResultsDetails /></AuthGuard>} />

        <Route path="/admin/attempt-details/:assignmentId" element={<AuthGuard requiredRole="admin"><ReviewAttemptDetails /></AuthGuard>} />

        <Route path="/admin/upload-english" element={<AuthGuard requiredRole="admin"><EnglishQuestionUpload /></AuthGuard>} />
        <Route path="/admin/upload-math" element={<AuthGuard requiredRole="admin"><MathQuestionUpload /></AuthGuard>} />
        <Route path="/admin/upload-science" element={<AuthGuard requiredRole="admin"><ScienceQuestionUpload /></AuthGuard>} />
        <Route path="/admin/adaptive-exam-builder" element={<AuthGuard requiredRole="admin"><AdaptiveExamBuilder /></AuthGuard>} />
        <Route path="/admin/flow-builder" element={<AuthGuard requiredRole="admin"><ExamFlowBuilder /></AuthGuard>} />
        <Route path="/admin/flow-builder-exams" element={<AuthGuard requiredRole="admin"><FlowBuilderExams /></AuthGuard>} />
        <Route path="/admin/flow-preview/:examId" element={<AuthGuard requiredRole="admin"><FlowExamPreview /></AuthGuard>} />

        <Route path="/admin/math-flow-builder" element={<AuthGuard requiredRole="admin"><MathExamFlowBuilder /></AuthGuard>} />
        <Route path="/admin/math-flow-builder-exams" element={<AuthGuard requiredRole="admin"><FlowBuilderExams /></AuthGuard>} />

        <Route path="/admin/science-flow-builder" element={<AuthGuard requiredRole="admin"><ScienceExamFlowBuilder /></AuthGuard>} />
        <Route path="/admin/science-flow-builder-exams" element={<AuthGuard requiredRole="admin"><FlowBuilderExams /></AuthGuard>} />

        <Route path="/admin/assign-student-to-parent" element={<AuthGuard requiredRole="admin"><AdminAssignStudentToParent /></AuthGuard>} />
        <Route path="/admin/create-parent" element={<AuthGuard requiredRole="admin"><AdminCreateParent /></AuthGuard>} />
        <Route path="/admin/manage-parent" element={<AuthGuard requiredRole="admin"><AdminManageParent /></AuthGuard>} />

        <Route path="/parent/dashboard" element={<AuthGuard requiredRole="parent"><ParentDashboard /></AuthGuard>} />
        <Route path="/parent/results/:assignmentId" element={<AuthGuard requiredRole="parent"><ParentResults /></AuthGuard>} />

        <Route path="/admin/manage-tags" element={<AuthGuard requiredRole="admin"><ManageTags /></AuthGuard>} />

        {/* ✅ Admin tutor routes (all map to the same component; it picks sub-view from the path) */}
        <Route path="/admin/add-tutor" element={<AuthGuard requiredRole="admin"><AdminTutorManagement /></AuthGuard>} />
        <Route path="/admin/manage-tutor" element={<AuthGuard requiredRole="admin"><AdminTutorManagement /></AuthGuard>} />
        <Route path="/admin/assign-student-to-tutor" element={<AuthGuard requiredRole="admin"><AdminTutorManagement /></AuthGuard>} />
        <Route path="/admin/assign-tutor" element={<AuthGuard requiredRole="admin"><AdminTutorManagement /></AuthGuard>} />

        {/* ✅ Tutor routes */}
        <Route path="/tutor/dashboard" element={<AuthGuard requiredRole="tutor"><TutorDashboard /></AuthGuard>} />
        <Route path="/tutor/students/:studentId" element={<AuthGuard requiredRole="tutor"><TutorStudentDetail /></AuthGuard>} />
        <Route path="/tutor/results/:assignmentId" element={<AuthGuard requiredRole="tutor"><ReviewAttemptDetails /></AuthGuard>} />

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
}

export default App;